// @LTG-ID: lib-use-sector
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 04b_SHARED_CONTRACT.md, 08_FEATURES.md#F-070
// @CHANGED: 2026-02 session_002
// @OWNER: web
//
// SectorProvider + useSector hook. Lifts the selected sector to a single React Context
// shared across all sector-aware components (CitationStrip, OwnerArchetype, SampleReport,
// Snapshot in P2, etc.).
//
// URL `?sector=<key>` is the canonical source of truth.
//   - On mount we read it (must be one of the 11 known keys).
//   - setSector(key) updates state AND writes via history.replaceState (no remount).
//   - listens to popstate so back/forward buttons stay in sync.

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { SECTOR_KEYS, getSector } from "../data/sectors";

const SectorContext = createContext({
    sector: null,
    sectorRecord: null,
    setSector: () => {},
});

function readSectorFromUrl() {
    if (typeof window === "undefined") return null;
    try {
        const params = new URLSearchParams(window.location.search);
        const k = params.get("sector");
        if (k && SECTOR_KEYS.includes(k)) return k;
    } catch (_e) {
        // ignore
    }
    return null;
}

function writeSectorToUrl(key) {
    if (typeof window === "undefined") return;
    try {
        const url = new URL(window.location.href);
        if (key) url.searchParams.set("sector", key);
        else url.searchParams.delete("sector");
        window.history.replaceState({}, "", url.toString());
    } catch (_e) {
        // ignore
    }
}

export function SectorProvider({ children }) {
    const [sector, setSectorState] = useState(null);

    // Initialize from URL on mount.
    useEffect(() => {
        const s = readSectorFromUrl();
        if (s) setSectorState(s);
    }, []);

    // Back/forward sync.
    useEffect(() => {
        function onPop() {
            setSectorState(readSectorFromUrl());
        }
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    const setSector = useCallback((key) => {
        const next = key && SECTOR_KEYS.includes(key) ? key : null;
        setSectorState(next);
        writeSectorToUrl(next);
    }, []);

    const value = {
        sector,
        sectorRecord: getSector(sector),
        setSector,
    };

    return (
        <SectorContext.Provider value={value}>{children}</SectorContext.Provider>
    );
}

export function useSector() {
    return useContext(SectorContext);
}
